import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import Conversation from './components/Conversation';
import Launcher from './components/Launcher';
import ThemeContext from './ThemeContext';
import MobileTakeover from './MobileTakeover';
import { useIsMobile, useIsTouchMobile } from '../../utils/use-is-mobile';
import { useVisualViewport } from '../../utils/use-visual-viewport';
import { useBodyScrollLock } from '../../utils/scroll-lock';
import { setFullScreen } from '../../store/actions';
import './style.scss';

const WidgetLayout = (props) => {
    const { zIndex, zIndexLauncher } = React.useContext(ThemeContext);

    const isMobile = useIsMobile();
    const isTouchMobile = useIsTouchMobile();
    const embeddedRootRef = React.useRef(null);

    // Mobile takeover (spec 2026-10-15): embedded mode takes over the visual
    // viewport once the composer was focused (fullScreenMode); floating mode is
    // already full-viewport while open and only needs keyboard-aware sizing +
    // host scroll lock. Once entered, the embedded takeover stays until the user
    // exits - a viewport that grows past the breakpoint must not drop it.
    const composerHasFocus = () => {
        // Scoped to this widget: the host is inside the root while inline,
        // which is when this check matters (the flag is sticky afterwards).
        const el = document.activeElement;
        const root = embeddedRootRef.current;
        return !!el && !!root && root.contains(el) && el.classList.contains('rw-new-message');
    };
    const requested = !!(props.embedded && props.fullScreenMode);
    const [embeddedTakeover, setEmbeddedTakeover] = React.useState(false);
    // Derived during render (not in an effect) so the host moves inside the
    // focus gesture's task - iOS drops the keyboard otherwise. Entering needs
    // the composer to actually hold focus.
    if (requested && !embeddedTakeover && isTouchMobile && composerHasFocus()) {
        setEmbeddedTakeover(true);
    }
    if (!requested && embeddedTakeover) {
        setEmbeddedTakeover(false);
    }
    // fullScreenMode seen on mobile without composer focus is a value persisted
    // from a previous page, not a user action: clear it so the page loads inline.
    React.useEffect(() => {
        if (requested && isTouchMobile && !embeddedTakeover && !composerHasFocus()) {
            props.dispatch(setFullScreen(false));
        }
    }, [requested, isTouchMobile, embeddedTakeover]);

    const isVisible = props.isChatVisible && !(props.hideWhenNotConnected && !props.connected);
    const chatShowing = props.isChatOpen || props.embedded;
    const floatingTakeover = !props.embedded && isMobile && chatShowing && isVisible;
    const floatingViewport = useVisualViewport(floatingTakeover);
    useBodyScrollLock(floatingTakeover);

    const exitTakeover = React.useCallback(() => {
        // Blur first: leaving fullscreen with the keyboard up would let Safari
        // re-pan the page to the still-focused textarea inside the host box.
        const active = document.activeElement;
        if (active && active.classList && active.classList.contains('rw-new-message')) {
            active.blur();
        }
        props.dispatch(setFullScreen(false));
    }, [props.dispatch]);

    const classes = props.embedded ? ['rw-widget-embedded'] : ['rw-widget-container'];
    if (props.fullScreenMode) {
        classes.push('rw-full-screen');
    }
    // Overlay mode: floating launcher that opens the conversation as a centered
    // modal over a dimmed, click-to-close backdrop. Only meaningful when not embedded.
    const overlayMode = props.overlayMode && !props.embedded;
    if (overlayMode) {
        classes.push('rw-overlay-mode');
    }
    // Chat window sized by distance from the viewport edges (non-embedded).
    // Only meaningful when not embedded; the mobile breakpoint keeps it
    // full-screen regardless (see conversation style.scss).
    const hasInset = !props.embedded && (props.chatWindowInsetX || props.chatWindowInsetY);
    if (hasInset) {
        classes.push('rw-chat-inset');
    }
    const showCloseButton =
        props.showCloseButton !== undefined ? props.showCloseButton : !props.embedded;

    if (chatShowing && !props.embedded) {
        classes.push('rw-chat-open');
    }

    const conversationCustomStyle = {};
    if (zIndex) {
        conversationCustomStyle.zIndex = zIndex;
    }
    if (hasInset) {
        if (props.chatWindowInsetX) {
            conversationCustomStyle['--rw-chat-inset-x'] = props.chatWindowInsetX;
        }
        if (props.chatWindowInsetY) {
            conversationCustomStyle['--rw-chat-inset-y'] = props.chatWindowInsetY;
        }
    }
    if (floatingTakeover) {
        // Inline beats every class rule (overlay-mode, full-screen variants).
        if (floatingViewport.keyboardOpen) {
            conversationCustomStyle.top = `${floatingViewport.top}px`;
            conversationCustomStyle.height = `${floatingViewport.height}px`;
            conversationCustomStyle.bottom = 'auto';
        } else {
            // Keyboard closed: let the browser span the layout viewport.
            conversationCustomStyle.top = '0px';
            conversationCustomStyle.bottom = '0px';
            conversationCustomStyle.height = 'auto';
        }
        // Host headers with a higher z-index would otherwise cover the close
        // button and trap the user behind a scroll-locked page.
        conversationCustomStyle.zIndex = 2147483647;
    }

    const launcherCustomStyle = {};
    if (zIndexLauncher) {
        launcherCustomStyle.zIndex = zIndexLauncher;
    }

    const wrapperStyle = props.embedded ? { width: '100%', height: '100%' } : {};

    const conversation = (
        <Conversation
            title={props.title}
            subtitle={props.subtitle}
            sendMessage={props.onSendMessage}
            toggleChat={props.toggleChat}
            isChatOpen={props.isChatOpen}
            toggleFullScreen={props.toggleFullScreen}
            fullScreenMode={props.fullScreenMode}
            disabledInput={props.disabledInput}
            params={props.params}
            showFullScreenButton={props.showFullScreenButton}
            {...{ showCloseButton }}
            connected={props.connected}
            connectingText={props.connectingText}
            closeImage={props.closeImage}
            customComponent={props.customComponent}
            showMessageDate={props.showMessageDate}
            inputTextFieldHint={props.inputTextFieldHint}
            showTitleBar={props.showTitleBar}
            compactCloseBar={floatingTakeover}
            embedded={props.embedded}
            mobileFullScreenOnFocus={props.mobileFullScreenOnFocus}
        />
    );

    return isVisible ? (
        <div style={wrapperStyle}>
            {overlayMode && chatShowing && (
                <div
                    className="rw-widget-overlay-backdrop"
                    onClick={props.toggleChat}
                    aria-hidden="true"
                />
            )}
            <div
                ref={embeddedRootRef}
                className={`rw-widget-conversation-container ${classes.join(' ')}`}
                style={conversationCustomStyle}
            >
                {chatShowing &&
                    (props.embedded ? (
                        <MobileTakeover
                            active={embeddedTakeover}
                            onExit={exitTakeover}
                            varsSource={embeddedRootRef}
                        >
                            {conversation}
                        </MobileTakeover>
                    ) : (
                        conversation
                    ))}
            </div>
            {/* Embedded mode has no launcher, and must not render its wrapper
                either: carrying .rw-widget-embedded it would be a second
                `height: 100%` box below the conversation, doubling the content
                height of the host's mount point. Inside an overflow-hidden host
                container the browser then scrolls that container instead of the
                page to reveal the focused input, and the user cannot scroll it
                back (youna prod on mobile). */}
            {!props.embedded && (
                <div
                    className={`rw-widget-launcher-container ${classes.join(' ')}`}
                    style={launcherCustomStyle}
                >
                    <Launcher
                        toggle={props.toggleChat}
                        isChatOpen={props.isChatOpen}
                        badge={props.badge}
                        fullScreenMode={props.fullScreenMode}
                        openLauncherImage={props.openLauncherImage}
                        closeImage={props.closeImage}
                        displayUnreadCount={props.displayUnreadCount}
                        tooltipPayload={props.tooltipPayload}
                        disablePreviewBubble={props.disablePreviewBubble}
                    />
                </div>
            )}
        </div>
    ) : null;
};

const mapStateToProps = (state) => ({
    isChatVisible: state.behavior.get('isChatVisible'),
    isChatOpen: state.behavior.get('isChatOpen'),
    disabledInput: state.behavior.get('disabledInput'),
    connected: state.behavior.get('connected'),
    connectingText: state.behavior.get('connectingText'),
});

WidgetLayout.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    onSendMessage: PropTypes.func,
    toggleChat: PropTypes.func,
    toggleFullScreen: PropTypes.func,
    //isChatOpen: PropTypes.bool,
    //isChatVisible: PropTypes.bool,
    showCloseButton: PropTypes.bool,
    showFullScreenButton: PropTypes.bool,
    hideWhenNotConnected: PropTypes.bool,
    disabledInput: PropTypes.bool,
    fullScreenMode: PropTypes.bool,
    badge: PropTypes.number,
    embedded: PropTypes.bool,
    overlayMode: PropTypes.bool,
    chatWindowInsetX: PropTypes.string,
    chatWindowInsetY: PropTypes.string,
    disablePreviewBubble: PropTypes.bool,
    inputTextFieldHint: PropTypes.string,
    params: PropTypes.object,
    connected: PropTypes.bool,
    connectingText: PropTypes.string,
    openLauncherImage: PropTypes.string,
    closeImage: PropTypes.string,
    customComponent: PropTypes.func,
    customData: PropTypes.any,
    displayUnreadCount: PropTypes.bool,
    showMessageDate: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    tooltipPayload: PropTypes.string,
    showTitleBar: PropTypes.bool,
    mobileFullScreenOnFocus: PropTypes.bool,
    dispatch: PropTypes.func,
};

export default connect(mapStateToProps)(WidgetLayout);
